import React from 'react';
import { Button, Icon, Label } from 'semantic-ui-react';
import moment from 'moment';
import { Margin, usePDF } from 'react-to-pdf';
import { copytoClipboard, scrollUp } from '../../../components/helpers';
import styled from '@emotion/styled';
import Swal from 'sweetalert2';
import groupService from '../../../services/api/groupService';
import { kindOfGroups, kindOfSeminaries, newLeadersQuestions } from './newLeadersQuestions';
import {
  condenseAnswers,
  guestData,
  questionType,
} from '../../../components/helpers/renderFormAnswers';

const ResultsComp = styled.div`
  background-color: white;
  min-width: 80%;

  .info {
    padding: 24px;

    .userdata {
      display: block;
      margin: 0.15rem 0;
      color: #444;
      line-height: 1.35;
    }
  }

  h1 {
    padding: 12px 0;
    margin: 0;
  }

  .content {
    .metadata {
      margin-bottom: 0.75rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #e9ecef;

      p {
        margin: 0.15rem 0;
        color: #444;
        line-height: 1.35;
      }
    }

    .prospect-summary {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      margin-bottom: 0.75rem;

      h2 {
        color: #2185d0;
        font-size: 1.15rem;
        margin: 0 0 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.35rem;
      }

      .guest-block {
        margin-bottom: 0.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid #eef2f6;

        &:last-of-type {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }

        h3 {
          color: #2c3e50;
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
      }
    }

    .leader-section {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      margin-bottom: 0.75rem;

      h2 {
        color: #2c3e50;
        font-size: 1.15rem;
        margin: 0 0 0.4rem;
        display: flex;
        align-items: center;
        gap: 0.35rem;
      }

      .leader-status {
        margin-bottom: 0.4rem;
      }

      .leader-details {
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 0.6rem 0.75rem;

        .detail-row {
          margin: 0.25rem 0;
          color: #444;
          line-height: 1.35;

          strong {
            color: #2c3e50;
          }
        }

        .recommendation {
          margin-top: 0.4rem;
          padding-top: 0.4rem;
          border-top: 1px solid #eef2f6;
        }

        .observations {
          margin-top: 0.4rem;
          padding: 0.5rem 0.6rem;
          background: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #2185d0;

          .observations-label {
            display: block;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 0.25rem;
            font-size: 0.95rem;
          }

          .observations-text {
            margin: 0;
            color: #555;
            font-style: italic;
            line-height: 1.4;
            white-space: pre-wrap;
          }
        }
      }

      .leader-pending {
        color: #666;
        line-height: 1.4;
        margin: 0;
        font-size: 0.95rem;
      }
    }

    .prospect-answers {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;

      > h2 {
        color: #2185d0;
        font-size: 1.15rem;
        margin: 0 0 0.25rem;
        display: flex;
        align-items: center;
        gap: 0.35rem;
      }

      .section-intro {
        color: #666;
        margin: 0 0 0.5rem;
        line-height: 1.35;
        font-size: 0.9rem;
      }

      .answers-empty {
        color: #888;
        font-style: italic;
        margin: 0;
        font-size: 0.9rem;
      }

      .answers-list {
        > .question {
          margin: 0.35rem 0;
          padding: 0.5rem 0.65rem;
          background: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #2185d0;

          > h2 {
            color: #2185d0;
            font-size: 1.05rem;
            margin: 0 0 0.25rem;
            padding: 0;
            border: none;
            background: transparent;
          }

          > h3 {
            color: #2c3e50;
            font-size: 0.95rem;
            font-weight: 600;
            margin: 0 0 0.3rem;
            line-height: 1.3;
          }

          > p:not(.answer) {
            color: #555;
            line-height: 1.4;
            margin: 0.25rem 0 0;
            font-size: 0.9rem;
          }

          .answer {
            margin: 0;
            padding: 0.35rem 0.5rem;
            background: #fff;
            border-radius: 4px;
            border: 1px solid #e9ecef;
            color: #333;
            font-style: normal;
            line-height: 1.35;
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 0.9rem;

            &.answer--empty {
              color: #999;
              font-style: italic;
              background: #fafbfc;
            }
          }

          .answer-list {
            margin: 0;
            padding: 0.35rem 0.5rem 0.35rem 1.25rem;
            background: #fff;
            border-radius: 4px;
            border: 1px solid #e9ecef;
            color: #333;
            line-height: 1.35;
            font-size: 0.9rem;

            li {
              margin: 0.1rem 0;
            }
          }

          img {
            max-height: 180px;
            border-radius: 6px;
            margin-top: 0.25rem;
            border: 1px solid #e9ecef;
          }
        }

        > .section-title {
          margin: 0.6rem 0 0.3rem;
          padding: 0.4rem 0 0;
          border-top: 1px solid #e9ecef;

          &:first-child {
            margin-top: 0;
            padding-top: 0;
            border-top: none;
          }

          h2 {
            color: #2185d0;
            font-size: 1.05rem;
            margin: 0;
          }
        }
      }
    }
  }
`;

const SKIP_ANSWER_TYPES = new Set([
  'prevButton',
  'nextButton',
  'send',
  'habeasData',
  'form',
  'form_male',
  'form_female',
]);

const getDisplayQuestions = (questions) =>
  questions?.flat()?.filter((question) => {
    if (question.textOnlyInQuestions || SKIP_ANSWER_TYPES.has(question.type)) return false;
    if (question.type === 'title') return true;
    return !!question.id;
  });

const getFormTypeLabel = (answers) =>
  answers?.info?.info_couple_seminary ??
  [...kindOfGroups, ...kindOfSeminaries]?.find((kind) => kind.value === answers?.type)?.text ??
  answers?.type;

const ProspectSummary = ({ answers }) => {
  const hasMale = !!answers.maleNewLeader?.id;
  const hasFemale = !!answers.femaleNewLeader?.id;

  if (!hasMale && !hasFemale) return null;

  return (
    <section className="prospect-summary">
      <h2>
        <Icon name="user" />
        Aspirante(s)
      </h2>
      {hasMale && (
        <div className="guest-block">
          <h3>Datos de él</h3>
          {guestData(answers.maleNewLeader)}
          {answers.maleNewLeader?.GuestLeader?.leaderFullName && (
            <p className="userdata">
              Líder de discipulado:{' '}
              <strong>{answers.maleNewLeader.GuestLeader.leaderFullName}</strong> —{' '}
              {answers.maleNewLeader.GuestLeader.leaderPhone}
            </p>
          )}
        </div>
      )}
      {hasFemale && (
        <div className="guest-block">
          <h3>Datos de ella</h3>
          {guestData(answers.femaleNewLeader)}
          {answers.femaleNewLeader?.GuestLeader?.leaderFullName && (
            <p className="userdata">
              Líder de discipulado:{' '}
              <strong>{answers.femaleNewLeader.GuestLeader.leaderFullName}</strong> —{' '}
              {answers.femaleNewLeader.GuestLeader.leaderPhone}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

const ProspectFormAnswers = ({ answers }) => {
  const questions = getDisplayQuestions(newLeadersQuestions[answers?.type]);

  if (!questions?.length) {
    return (
      <section className="prospect-answers">
        <h2>
          <Icon name="file alternate outline" />
          Respuestas del formulario
        </h2>
        <p className="answers-empty">No hay respuestas del formulario disponibles.</p>
      </section>
    );
  }

  return (
    <section className="prospect-answers">
      <h2>
        <Icon name="file alternate outline" />
        Respuestas del formulario
      </h2>
      <p className="section-intro">
        Preguntas diligenciadas por el aspirante al enviar la solicitud.
      </p>
      <div className="answers-list">
        {questions.map((question) => {
          if (question.type === 'title') {
            return (
              <div key={question.id || question.text} className="section-title">
                {questionType(question)}
              </div>
            );
          }

          return (
            <div key={question.id} className={`${question.class || ''} question`}>
              {questionType(question)}
              {formatAnswerForDisplay(answers, question)}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const formatAnswerForDisplay = (answers, question) => {
  const condensed = condenseAnswers(answers);
  const content = condensed?.[question.id];

  if (question.type === 'date') {
    const formatted =
      content && moment(content).isValid() ? moment(content).format('DD-MM-YYYY') : null;
    return (
      <p className={`answer ${!formatted ? 'answer--empty' : ''}`}>
        {formatted || 'Sin respuesta'}
      </p>
    );
  }

  if (Array.isArray(content)) {
    if (!content.length) return <p className="answer answer--empty">Sin respuesta</p>;
    return (
      <ul className="answer-list">
        {content.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  if (content === undefined || content === null || content === '') {
    return <p className="answer answer--empty">Sin respuesta</p>;
  }

  return <p className="answer">{String(content)}</p>;
};

const LeaderSurveySummary = ({ answers }) => {
  const leaderCompleted = typeof answers?.survey === 'boolean';
  const linkToLeader = `${window.location?.origin}/grupos/aspirante/${answers?.leader_token}`;
  const recommendsContinue = answers?.survey;

  return (
    <section className="leader-section">
      <h2>
        <Icon name="clipboard check" />
        Encuesta del líder
      </h2>
      <div className="leader-status">
        {leaderCompleted ? (
          <Label color="green" size="small">
            <Icon name="check circle" />
            Completada
          </Label>
        ) : (
          <Label color="orange" size="small">
            <Icon name="clock outline" />
            Pendiente
          </Label>
        )}
      </div>
      {leaderCompleted ? (
        <div className="leader-details">
          {answers?.leader_observations?.name && (
            <>
              <p className="detail-row">
                Líder encuestado:{' '}
                <strong>
                  {answers.leader_observations.name} {answers.leader_observations.lastName}
                </strong>
              </p>
              <p className="detail-row">
                Identificación: <strong>{answers.leader_observations.identification}</strong>
              </p>
            </>
          )}
          <p className="detail-row recommendation">
            ¿Recomienda continuar con el proceso?:{' '}
            <Label color={recommendsContinue ? 'green' : 'red'}>
              {recommendsContinue ? 'Sí' : 'No'}
            </Label>
          </p>
          {!!answers?.leader_observations?.observations && (
            <div className="observations">
              <span className="observations-label">Observaciones del líder</span>
              <p className="observations-text">{answers.leader_observations.observations}</p>
            </div>
          )}
        </div>
      ) : (
        !!answers?.leader_token && (
          <Button
            color="black"
            onClick={() => copytoClipboard({ content: linkToLeader, Swal })}
            style={{ display: 'block', margin: '4px 0' }}
          >
            Copiar link para enviar al líder
          </Button>
        )
      )}
    </section>
  );
};

const NewLeaderAnswers = (props) => {
  const { answers, set_showAnswers, getInfo } = props;
  const [sending, set_sending] = React.useState(false);
  const { toPDF, targetRef } = usePDF({
    method: 'save',
    page: { margin: Margin.MEDIUM },
    filename: 'respuestas-nuevo-aspirante-grupos.pdf',
  });

  React.useEffect(() => {
    scrollUp();
  }, []);

  const setVars = (toUpdate) => {
    let body = {
      id: answers?.id,
    };
    if (toUpdate === 'deleted') body.deleted = !answers?.deleted;

    set_sending(true);
    groupService
      .updateNewLeaderForm(body)
      .then((response) => {
        set_sending(false);
        if (response.data.success) {
          getInfo();
        }
        Swal.fire(response?.data?.message);
      })
      .catch((error) => {
        set_sending(false);
        Swal.fire(error?.response?.data?.message);
      });
  };

  const deleteProspectSurvey = () =>
    Swal.fire({
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Eliminar',
      showCancelButton: true,
      title: `¿Está seguro que quieres eliminar esta encuesta?`,
    }).then(async (result) => {
      if (result.value) {
        setVars('deleted');
        set_showAnswers(null);
      }
    });

  return (
    <ResultsComp>
      <div className="info">
        <Button
          primary
          compact
          disabled={sending}
          onClick={() => set_showAnswers(null)}
          style={{ display: 'block', margin: '4px 0' }}
        >
          Ir Atrás
        </Button>
        <h1>Respuestas</h1>
        <div style={{ margin: '4px 0' }}>
          <Button disabled={sending} onClick={deleteProspectSurvey}>
            Eliminar encuesta
          </Button>
        </div>
        <Button onClick={toPDF}>Guardar formulario</Button>
        {!answers?.leader_token && !!answers?.leader_observations?.name && (
          <p className="userdata">
            Link del líder: <strong>Ya utilizado</strong>
          </p>
        )}
        <div ref={targetRef} className="printable content">
          <div className="metadata">
            <p>
              Tipo de formulario: <strong>{getFormTypeLabel(answers)}</strong>
            </p>
            <p>
              Fecha de llenado:{' '}
              <strong>{moment.tz(answers.createdAt, 'America/Bogota').format('DD-MM-YYYY')}</strong>
            </p>
          </div>

          <ProspectSummary answers={answers} />
          <LeaderSurveySummary answers={answers} />

          <ProspectFormAnswers answers={answers} />
        </div>
      </div>
    </ResultsComp>
  );
};
export default NewLeaderAnswers;
